import {
  Box,
  NumberInput as ChakraNumberInput,
  InputGroup,
  Span,
  Field,
  InputProps as ChakraInputProps,
} from '@chakra-ui/react';
import * as React from 'react';
import { LuArrowRightLeft } from 'react-icons/lu';
import { labelStyles } from './label';
import { InfoTip, Alert, SkeletonLoading } from '.';

export interface NumberInputProps {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

  disabled?: boolean;
  triggerResetToDefault?: number;
  label?: React.ReactNode;
  infoTip?: React.ReactNode;
  placeholder?: string;
  placeholderStyle?: ChakraInputProps['_placeholder'];
  size?: ChakraInputProps['size'];
  children?: React.ReactNode;
  isLoading?: boolean;
  [key: string]: any;
}

export const NumberInput = React.forwardRef<HTMLDivElement, NumberInputProps>(
  function NumberInput(props, ref) {
    const {
      value,
      defaultValue = 0,
      min = 0,
      max = 999999,
      triggerResetToDefault = 0,
      isLoading = false,
      children,
      onChange,
      onBlur,
      disabled,
      label,
      infoTip,
      placeholder,
      placeholderStyle = {
        color: 'fg.subtle',
        textStyle: 'caption.muted',
        fontSize: 'xs',
      },
      size = 'xs',
      ...rest
    } = props;

    const [inputValue, setInputValue] = React.useState<String>(
      String(defaultValue),
    );
    const inputRef = React.useRef<HTMLInputElement>(null);
    const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);
    React.useEffect(() => {
      if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
        setInputValue(String(defaultValue));
        onChange?.(Number(defaultValue));
        triggerResetToDefaultRef.current = triggerResetToDefault;
      }
    }, [triggerResetToDefault]);

    React.useEffect(() => {
      if (value !== undefined && value !== inputValue) {
        setInputValue(String(value));
      }
    }, [value]);

    const handleChange = (e: String) => {
      setInputValue(e);
      onChange?.(Number(e));
    };

    return (
      <Field.Root>
        {isLoading ? (
          <SkeletonLoading noOfLines={1} />
        ) : (
          <Box pos="relative" w="full">
            <ChakraNumberInput.Root
              ref={inputRef}
              variant="outline"
              width="100%"
              min={min}
              max={max}
              value={String(inputValue)}
              onChange={(e: any) => {
                handleChange(e.target?.value as unknown as String);
              }}
              onValueChange={(e: any) => {
                handleChange(e.value as unknown as String);
              }}
              onBlur={onBlur}
              size={size}
              disabled={disabled}
              {...rest}
            >
              {children}
              <ChakraNumberInput.Control>
                {/* <ChakraNumberInput.IncrementTrigger />
          <ChakraNumberInput.DecrementTrigger /> */}
              </ChakraNumberInput.Control>
              <InputGroup
                startElementProps={{ pointerEvents: 'auto' }}
                startElement={
                  <ChakraNumberInput.Scrubber>
                    <LuArrowRightLeft />
                  </ChakraNumberInput.Scrubber>
                }
              >
                <ChakraNumberInput.Input />
              </InputGroup>
            </ChakraNumberInput.Root>
            {label && (
              <Field.Label
                {...(labelStyles.floating({}) as any)}
                disabled={disabled}
              >
                {label}{' '}
                {infoTip && (
                  <Span pointerEvents="auto">
                    <InfoTip>
                      <Alert>{infoTip}</Alert>
                    </InfoTip>
                  </Span>
                )}
              </Field.Label>
            )}
          </Box>
        )}
      </Field.Root>
    );
  },
);

export const NumberInputField = ChakraNumberInput.Input;
export const NumberInputScrubber = ChakraNumberInput.Scrubber;
export const NumberInputLabel = ChakraNumberInput.Label;
